import { useState } from "react";
import { Modal, ModalBody, Form, FormGroup, Label, Input, FormFeedback } from "reactstrap";
import { Btn, H2 } from "../../../AbstractElements";
import { toast } from "react-toastify";
import axiosInstance from "../../../Utils/axiosInstance";
import { backendbaseUrl } from "../../../Utils/baseUrl";
import { IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { passwordPolicy } from "../../../Helpers/Password.helper";
import "../../../Components/SharedComponents/InputIcons.scss"

interface Props {
    modalOpen: boolean;
    toggle: () => void;
    userEmail: string;
}

export default function AdminResetPasswordModal({ modalOpen, toggle, userEmail }: Props) {
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({ newPassword: "", confirmPassword: "" });

    const validate = (): boolean => {
        const newErrors = { newPassword: "", confirmPassword: "" };
        let valid = true;

        const policyError = passwordPolicy(newPassword);
        if (!newPassword) {
            newErrors.newPassword = "New password is required";
            valid = false;
        } else if (policyError) {
            newErrors.newPassword = policyError;
            valid = false;
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = "Please re-enter your new password";
            valid = false;
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = "Passwords do not match";
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleClose = () => {
        setNewPassword("");
        setConfirmPassword("");
        setErrors({ newPassword: "", confirmPassword: "" });
        setShowNew(false);
        setShowConfirm(false);
        toggle();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            const response = await axiosInstance.post(`${backendbaseUrl}/users/admin/reset-password/`, {
                email: userEmail,
                new_password: newPassword,
            });

            if (response.status === 200) {
                toast.success("Password reset successfully!");
                handleClose();
            }
        } catch (error: any) {
            const data = error.response?.data;
            const errorMsg: string = data?.error || data?.message || data?.detail || "";

            if (errorMsg.toLowerCase().includes("password") || data?.new_password) {
                setErrors((prev) => ({
                    ...prev,
                    newPassword: data?.new_password?.[0] || errorMsg || "Invalid password",
                }));
            } else {
                toast.error(errorMsg || "Something went wrong");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={modalOpen} toggle={handleClose} centered>
            <ModalBody className="p-4">
                <H2 className="mb-1 text-center">Reset User Password</H2>
                <p className="text-muted f-12 mb-4">{userEmail}</p>

                <Form onSubmit={handleSubmit} noValidate>
                    <FormGroup>
                        <Label>New Password</Label>
                        <div className="form-input position-relative">
                            <Input
                                type={showNew ? "text" : "password"}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter New Password"
                                invalid={!!errors.newPassword}
                                style={{ paddingRight: "44px" }}
                            />
                            <div className="icon-wrapper">
                                <IconButton
                                    onClick={() => setShowNew(!showNew)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    edge="end"
                                    size="small"
                                    aria-label={showNew ? "Hide password" : "Show password"}
                                    className="eye-button"
                                >
                                    {showNew ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                            </div>
                            <FormFeedback>{errors.newPassword}</FormFeedback>
                        </div>
                    </FormGroup>

                    {/* Confirm Password */}
                    <FormGroup>
                        <Label>Re-enter Password</Label>
                        <div className="form-input position-relative">
                            <Input
                                type={showConfirm ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Re-enter New Password"
                                invalid={!!errors.confirmPassword}
                                style={{ paddingRight: "44px" }}
                            />
                            <div className="icon-wrapper">
                                <IconButton
                                    onClick={() => setShowConfirm(!showConfirm)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    edge="end"
                                    size="small"
                                    aria-label={showConfirm ? "Hide password" : "Show password"}
                                    className="eye-button"
                                >
                                    {showConfirm ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                            </div>
                            <FormFeedback>{errors.confirmPassword}</FormFeedback>
                        </div>
                    </FormGroup>

                    <div className="d-flex gap-2 mt-3">
                        <Btn color="primary" style={{ flex: 1, whiteSpace: "nowrap" }} block disabled={loading}>
                            {loading ? "Resetting..." : "RESET PASSWORD"}
                        </Btn>
                        <Btn color="light" style={{ flex: 1 }} block type="button" onClick={handleClose}>
                            CANCEL
                        </Btn>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
}